import React, { useState } from 'react';
import {
  Box,
  Container,
  Grid,
  Typography,
  Link,
  IconButton,
  Divider,
  TextField,
  Button,
  Chip,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import FacebookIcon from '@mui/icons-material/Facebook';
import TwitterIcon from '@mui/icons-material/Twitter';
import InstagramIcon from '@mui/icons-material/Instagram';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import SendIcon from '@mui/icons-material/Send';
import VerifiedIcon from '@mui/icons-material/Verified';
import Logo from './Logo';

const socialLinks = [
  { icon: FacebookIcon, url: '#', name: 'Facebook',  color: '#1877f2' },
  { icon: TwitterIcon,  url: '#', name: 'Twitter',   color: '#1da1f2' },
  { icon: InstagramIcon,url: '#', name: 'Instagram', color: '#e4405f' },
  { icon: LinkedInIcon, url: '#', name: 'LinkedIn',  color: '#0a66c2' },
];

const serviceLinks = [
  { text: 'Laptop Repair',          path: '/laptop-repair' },
  { text: 'Mobile Repair',          path: '/mobile-repair' },
  { text: 'Desktop Service',        path: '/desktop-service' },
  { text: 'Cybersecurity',          path: '/cybersecurity' },
  { text: 'Microsoft 365 & Azure',  path: '/microsoft-365-cloud' },
  { text: 'Network Installation',   path: '/network-installation' },
  { text: 'Server Administration',  path: '/server-administration' },
  { text: 'Technical Support',      path: '/technical-support' },
];

const quickLinks = [
  { text: 'Home',           path: '/' },
  { text: 'Products',       path: '/products' },
  { text: 'Services',       path: '/services' },
  { text: 'Pricing',        path: '/pricing' },
  { text: 'About Us',       path: '/about' },
  { text: 'Blog',           path: '/blog' },
  { text: 'FAQ',            path: '/faq' },
  { text: 'Contact',        path: 'https://akdmetech.com/contact', external: true },
  { text: 'Track Repair',   path: '/track-repair' },
  { text: 'Get a Quote',    path: '/quote' },
  { text: 'Privacy Policy', path: '/privacy' },
];

const hours = [
  { day: 'Mon – Fri', time: '9:00 AM – 7:00 PM' },
  { day: 'Saturday',  time: '10:00 AM – 5:00 PM' },
  { day: 'Sunday',    time: 'By Appointment' },
];

const certBadges = [
  'CompTIA CySA+',
  'Microsoft 365',
  'Azure / Entra ID',
  'Cisco ISE',
];

function FooterLink({ path, text, external }) {
  return (
    <Link
      component={external ? 'a' : RouterLink}
      {...(external ? { href: path, target: '_blank', rel: 'noopener noreferrer' } : { to: path })}
      underline="none"
      sx={{
        display: 'block',
        color: 'rgba(255,232,232,0.6)',
        fontSize: '0.875rem',
        py: 0.4,
        transition: 'color 0.2s ease, padding-left 0.2s ease',
        '&:hover': {
          color: '#ff6b6b',
          pl: 0.5,
        },
      }}
    >
      {text}
    </Link>
  );
}

export default function Footer() {
  const [email, setEmail] = useState('');
  const [subscribed, setSubscribed] = useState(false);

  const handleSubscribe = (e) => {
    e.preventDefault();
    if (email.trim()) {
      setSubscribed(true);
      setEmail('');
    }
  };

  return (
    <Box
      component="footer"
      sx={{
        background: 'linear-gradient(180deg, #1a1f21 0%, #111416 100%)',
        borderTop: '1px solid rgba(255,107,107,0.15)',
        mt: 'auto',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Subtle top glow */}
      <Box sx={{
        position: 'absolute', top: 0, left: '50%', transform: 'translateX(-50%)',
        width: 600, height: 1,
        boxShadow: '0 0 80px 6px rgba(255,107,107,0.25)',
        pointerEvents: 'none',
      }} />

      {/* Main footer content */}
      <Container maxWidth="lg" sx={{ pt: 8, pb: 5 }}>
        <Grid container spacing={5}>

          {/* Col 1 — Brand */}
          <Grid item xs={12} md={3.5}>
            <Box sx={{ mb: 2 }}>
              <Logo size="medium" />
            </Box>
            <Typography variant="body2" sx={{ color: 'rgba(255,232,232,0.55)', lineHeight: 1.9, mb: 3, maxWidth: 280 }}>
              Enterprise-grade IT services for homes and small businesses in Lynn, MA.
              Hardware repair, cybersecurity, cloud services, and more — backed by 16+ years of experience.
            </Typography>

            {/* Cert badges */}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.8, mb: 3 }}>
              {certBadges.map((b) => (
                <Chip
                  key={b}
                  icon={<VerifiedIcon sx={{ fontSize: '0.8rem !important', color: '#ffd93d !important' }} />}
                  label={b}
                  size="small"
                  sx={{
                    background: 'rgba(255,217,61,0.08)',
                    border: '1px solid rgba(255,217,61,0.2)',
                    color: 'rgba(255,232,232,0.75)',
                    fontSize: '0.72rem',
                  }}
                />
              ))}
            </Box>

            {/* Social icons */}
            <Box sx={{ display: 'flex', gap: 1 }}>
              {socialLinks.map((s) => (
                <IconButton
                  key={s.name}
                  component="a"
                  href={s.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={s.name}
                  size="small"
                  sx={{
                    color: 'rgba(255,232,232,0.5)',
                    border: '1px solid rgba(255,255,255,0.08)',
                    borderRadius: '8px',
                    width: 36, height: 36,
                    transition: 'all 0.25s ease',
                    '&:hover': {
                      color: s.color,
                      borderColor: s.color,
                      background: `${s.color}18`,
                      transform: 'translateY(-3px)',
                      boxShadow: `0 4px 12px ${s.color}40`,
                    },
                  }}
                >
                  <s.icon sx={{ fontSize: 18 }} />
                </IconButton>
              ))}
            </Box>
          </Grid>

          {/* Col 2 — Services */}
          <Grid item xs={6} sm={4} md={2.5}>
            <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: '0.95rem', mb: 2, letterSpacing: 0.5 }}>
              Our Services
            </Typography>
            {serviceLinks.map((l) => <FooterLink key={l.path} {...l} />)}
          </Grid>

          {/* Col 3 — Quick Links */}
          <Grid item xs={6} sm={4} md={2}>
            <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: '0.95rem', mb: 2, letterSpacing: 0.5 }}>
              Quick Links
            </Typography>
            {quickLinks.map((l) => <FooterLink key={l.path} {...l} />)}
          </Grid>

          {/* Col 4 — Contact + Hours + Newsletter */}
          <Grid item xs={12} sm={4} md={4}>
            <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: '0.95rem', mb: 2, letterSpacing: 0.5 }}>
              Contact & Hours
            </Typography>

            {/* Contact info */}
            {[
              { icon: LocationOnIcon, text: '102 River Street, Lynn, MA 01905', href: 'https://maps.google.com/?q=102+River+Street+Lynn+MA' },
              { icon: PhoneIcon,      text: '(617) 751-8481',                    href: 'tel:+16177518481' },
              { icon: EmailIcon,      text: 'info@akdmetech.com',                 href: 'mailto:info@akdmetech.com' },
            ].map(({ icon: Icon, text, href }) => (
              <Box
                key={text}
                component="a"
                href={href}
                target={href.startsWith('http') ? '_blank' : undefined}
                rel={href.startsWith('http') ? 'noopener noreferrer' : undefined}
                sx={{
                  display: 'flex', alignItems: 'flex-start', gap: 1.2,
                  mb: 1.5, textDecoration: 'none',
                  '&:hover .footer-contact-text': { color: '#ff6b6b' },
                }}
              >
                <Icon sx={{ fontSize: 17, color: '#ff6b6b', mt: '2px', flexShrink: 0 }} />
                <Typography className="footer-contact-text" variant="body2" sx={{ color: 'rgba(255,232,232,0.6)', lineHeight: 1.6, transition: 'color 0.2s' }}>
                  {text}
                </Typography>
              </Box>
            ))}

            {/* Business hours */}
            <Box sx={{ mt: 2.5, mb: 3, p: 2, borderRadius: '10px', background: 'rgba(255,107,107,0.05)', border: '1px solid rgba(255,107,107,0.1)' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                <AccessTimeIcon sx={{ fontSize: 16, color: '#ffd93d' }} />
                <Typography sx={{ color: '#ffd93d', fontWeight: 600, fontSize: '0.82rem', letterSpacing: 0.5 }}>
                  Business Hours
                </Typography>
              </Box>
              {hours.map((h) => (
                <Box key={h.day} sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.6 }}>
                  <Typography variant="caption" sx={{ color: 'rgba(255,232,232,0.55)' }}>{h.day}</Typography>
                  <Typography variant="caption" sx={{ color: 'rgba(255,232,232,0.8)', fontWeight: 500 }}>{h.time}</Typography>
                </Box>
              ))}
            </Box>

            {/* Newsletter */}
            <Typography sx={{ color: '#fff', fontWeight: 600, fontSize: '0.88rem', mb: 1.2 }}>
              Stay in the Loop
            </Typography>
            {subscribed ? (
              <Typography variant="body2" sx={{ color: '#4ecca3', display: 'flex', alignItems: 'center', gap: 0.8 }}>
                <VerifiedIcon sx={{ fontSize: 16 }} /> Thanks! You're subscribed.
              </Typography>
            ) : (
              <Box component="form" onSubmit={handleSubscribe} sx={{ display: 'flex', gap: 1 }}>
                <TextField
                  type="email"
                  placeholder="Your email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  size="small"
                  required
                  sx={{
                    flex: 1,
                    '& .MuiOutlinedInput-root': {
                      color: '#ffe8e8',
                      fontSize: '0.85rem',
                      background: 'rgba(255,255,255,0.04)',
                      borderRadius: '8px',
                      '& fieldset': { borderColor: 'rgba(255,107,107,0.25)' },
                      '&:hover fieldset': { borderColor: 'rgba(255,107,107,0.5)' },
                      '&.Mui-focused fieldset': { borderColor: '#ff6b6b' },
                    },
                    '& input::placeholder': { color: 'rgba(255,232,232,0.35)', opacity: 1 },
                  }}
                />
                <Button
                  type="submit"
                  variant="contained"
                  sx={{
                    background: 'linear-gradient(135deg, #ff6b6b 0%, #ffd93d 100%)',
                    color: '#1a1f21',
                    fontWeight: 700,
                    px: 1.5,
                    minWidth: 0,
                    borderRadius: '8px',
                    '&:hover': { opacity: 0.9 },
                  }}
                >
                  <SendIcon sx={{ fontSize: 17 }} />
                </Button>
              </Box>
            )}
          </Grid>
        </Grid>
      </Container>

      <Divider sx={{ borderColor: 'rgba(255,107,107,0.1)' }} />

      {/* Bottom bar */}
      <Container maxWidth="lg">
        <Box sx={{
          py: 2.5,
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1.5,
        }}>
          <Typography variant="caption" sx={{ color: 'rgba(255,232,232,0.4)' }}>
            © {new Date().getFullYear()} AkdmeTech. All rights reserved. · Lynn, MA 01905
          </Typography>
          <Box sx={{ display: 'flex', gap: 2.5 }}>
            {[
              { text: 'Privacy Policy', path: '/privacy' },
              { text: 'Book a Service', path: '/booking' },
              { text: 'Track Repair',   path: '/track-repair' },
            ].map((l) => (
              <Link
                key={l.path}
                component={RouterLink}
                to={l.path}
                underline="none"
                sx={{
                  color: 'rgba(255,232,232,0.4)',
                  fontSize: '0.78rem',
                  transition: 'color 0.2s',
                  '&:hover': { color: '#ff6b6b' },
                }}
              >
                {l.text}
              </Link>
            ))}
          </Box>
        </Box>
      </Container>
    </Box>
  );
}
